import { useMemo } from 'react';

import {
    LOCATION_HELP,
    LOCATION_HELP_ORDER,
    detectBrowserPlatform,
    type LocationHelpGuide,
    type LocationProblem,
} from '../../utils/locationHelp';

function Steps({ steps }: { steps: string[] }) {
    return (
        <ol className="list-decimal space-y-1 pl-5 text-ink-700">
            {steps.map(step => <li key={step}>{step}</li>)}
        </ol>
    );
}

export function currentLocationGuide(): LocationHelpGuide {
    return LOCATION_HELP[detectBrowserPlatform(navigator.userAgent, navigator.maxTouchPoints ?? 0)];
}

const DEVICE_CASE = 'Điện thoại chưa cho trình duyệt dùng vị trí (hoặc đang tắt định vị)';
const SITE_CASE = 'Lỡ bấm "Không cho phép" khi trang hỏi vị trí';

const INTRO: Record<LocationProblem, string> = {
    'in-app': 'Bạn đang mở trang trong Zalo/Facebook, nơi thường không lấy được vị trí.',
    'site-blocked': 'Có thể bạn đã bấm "Không cho phép" khi trang hỏi vị trí. Trình duyệt sẽ không tự hỏi lại, bạn cần bật lại:',
    'device-blocked': 'Trang đã được phép, nhưng điện thoại đang không cho trình duyệt dùng vị trí (hoặc đang tắt định vị). Bật lại trong cài đặt của máy:',
    'blocked-unknown': 'Vị trí đang bị chặn. Thường do một trong hai trường hợp dưới đây, hãy kiểm tra lần lượt:',
    'device-off': 'Máy chưa xác định được vị trí. Kiểm tra định vị của máy đang bật, rồi ra gần cửa sổ hoặc bật Wi-Fi để bắt tín hiệu tốt hơn:',
    timeout: 'Định vị mất quá lâu. Ra gần cửa sổ hoặc bật Wi-Fi rồi bấm "Thử lại".',
    unsupported: 'Trình duyệt này không hỗ trợ định vị. Hãy mở trang bằng Chrome hoặc Safari.',
};

function stepsFor(problem: LocationProblem, guide: LocationHelpGuide) {
    if (problem === 'site-blocked' || problem === 'in-app') return guide.site;
    if (problem === 'device-blocked' || problem === 'device-off') return guide.device;
    return null;
}

// Hướng dẫn khi điểm danh không lấy được vị trí, tách theo nguyên nhân và theo thiết bị đang dùng.
export function LocationPermissionHelp({ problem }: { problem: LocationProblem }) {
    const guide = useMemo(currentLocationGuide, []);
    const others = LOCATION_HELP_ORDER.filter(platform => platform !== guide.platform).map(platform => LOCATION_HELP[platform]);
    const mainSteps = stepsFor(problem, guide);
    // Trình duyệt không luôn phân biệt được hai trường hợp, nên luôn cho xem trường hợp còn lại.
    const otherCase = problem === 'site-blocked'
        ? { title: DEVICE_CASE, steps: guide.device }
        : problem === 'device-blocked' || problem === 'device-off'
            ? { title: SITE_CASE, steps: guide.site }
            : null;
    const showsGuides = problem !== 'timeout' && problem !== 'unsupported';

    return (
        <div className="space-y-3 rounded-lg border border-ink-200 p-4 text-sm" aria-label="Cách bật lại quyền vị trí">
            <p className="text-ink-700">{INTRO[problem]}</p>

            {mainSteps ? (
                <div className="space-y-1.5">
                    <p className="font-semibold text-ink-900">{guide.title}</p>
                    <Steps steps={mainSteps} />
                </div>
            ) : null}

            {problem === 'blocked-unknown' ? (
                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <p className="font-semibold text-ink-900">Trường hợp 1: {DEVICE_CASE}</p>
                        <Steps steps={guide.device} />
                    </div>
                    <div className="space-y-1.5">
                        <p className="font-semibold text-ink-900">Trường hợp 2: {SITE_CASE}</p>
                        <Steps steps={guide.site} />
                    </div>
                    <p className="text-xs text-ink-500">Hướng dẫn cho {guide.title}.</p>
                </div>
            ) : null}

            {showsGuides && !guide.needsReload ? (
                <p className="text-xs text-ink-500">Bật xong, quay lại đây: trang sẽ tự định vị lại (hoặc bấm "Thử lại").</p>
            ) : null}

            {otherCase ? (
                <details className="text-ink-700">
                    <summary className="cursor-pointer text-academy-700">Vẫn không được? {otherCase.title}</summary>
                    <div className="mt-2">
                        <Steps steps={otherCase.steps} />
                    </div>
                </details>
            ) : null}

            {showsGuides && problem !== 'in-app' ? (
                <details className="text-ink-700">
                    <summary className="cursor-pointer text-academy-700">Hướng dẫn cho thiết bị khác</summary>
                    <div className="mt-2 space-y-4">
                        {others.map(other => (
                            <div key={other.platform} className="space-y-1.5">
                                <p className="font-medium text-ink-900">{other.title}</p>
                                {other.platform === 'in-app' ? <Steps steps={other.site} /> : (
                                    <>
                                        <p className="text-xs text-ink-500">{SITE_CASE}:</p>
                                        <Steps steps={other.site} />
                                        <p className="text-xs text-ink-500">{DEVICE_CASE}:</p>
                                        <Steps steps={other.device} />
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </details>
            ) : null}
        </div>
    );
}
